import { headingToId } from "./slugify";

export interface TocItem {
  id: string;
  level: number;
  text: string;
  children?: TocItem[];
}

/**
 * Extract headings from markdown content and generate table of contents
 */
export function generateTOC(content: string): TocItem[] {
  const headingRegex = /^(#{2,3})\s+(.+)$/gm;
  const headings: TocItem[] = [];
  const stack: TocItem[] = [];

  let match;
  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length; // Number of # characters
    const text = match[2].trim();
    const id = headingToId(text);

    const item: TocItem = {
      id,
      level,
      text,
    };

    // Remove items from stack that are at same or deeper level
    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }

    if (stack.length === 0) {
      // Top-level item
      headings.push(item);
      stack.push(item);
    } else {
      // Nested item
      const parent = stack[stack.length - 1];
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(item);
      stack.push(item);
    }
  }

  return headings;
}

/**
 * Find the active heading based on scroll position
 */
export function getActiveHeadingId(
  headings: TocItem[],
  scrollOffset = 100,
): string | null {
  const headingElements = headings
    .flatMap((h) => [h, ...(h.children || [])])
    .map((h) => {
      const element = document.getElementById(h.id);
      return element ? { id: h.id, offsetTop: element.offsetTop } : null;
    })
    .filter((h): h is { id: string; offsetTop: number } => h !== null)
    .sort((a, b) => b.offsetTop - a.offsetTop); // Sort descending

  const currentScroll = window.scrollY + scrollOffset;

  for (const heading of headingElements) {
    if (heading.offsetTop <= currentScroll) {
      return heading.id;
    }
  }

  return headingElements[headingElements.length - 1]?.id || null;
}
